import { useState, useEffect } from "react";
import { Plus, Trash, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@radix-ui/react-dialog";
import api from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ChangeLogPointTags } from "../../lib/constants";
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { CustomSelect } from "../ui/custom-select";

interface Section {
  title: string;
  descriptions: Array<{
    text: string;
    tag?: string;
  }>;
}

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
  changelog?: any;
  onSave: () => void;
}

export default function ChangelogModal({
  isOpen,
  onClose,
  changelog,
  onSave,
}: ChangelogModalProps) {
  const [version, setVersion] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [sections, setSections] = useState<Section[]>([]);
  const { user } = useAuth();
  useEffect(() => {
    if (changelog) {
      setVersion(changelog.version);
      setReleaseDate(changelog.release_date.split("T")[0]);
      setSections(changelog.content);
    }
  }, [changelog]);

  const addSection = () => {
    setSections([
      ...sections,
      { title: "", descriptions: [{ text: "", tag: "" }] },
    ]);
  };

  const addDescription = (sectionIndex: number) => {
    const newSections = [...sections];
    newSections[sectionIndex].descriptions.push({ text: "", tag: "" });
    setSections(newSections);
  };

  const removeSection = (sectionIndex: number) => {
    setSections(sections.filter((_, index) => index !== sectionIndex));
  };

  const removeDescription = (sectionIndex: number, descIndex: number) => {
    const newSections = [...sections];
    newSections[sectionIndex].descriptions = newSections[
      sectionIndex
    ].descriptions.filter((_, index) => index !== descIndex);
    setSections(newSections);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (changelog) {
      await api.updateChangeLog({
        id: changelog.id,
        version,
        release_date: releaseDate,
        content: sections,
      });
    } else {
      await api.createChangeLog({
        version,
        release_date: releaseDate,
        content: sections,
        created_by: user?.id,
      });
    }
    onSave();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogContent className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-900 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
            <div className="flex justify-between items-center">
              <DialogTitle>
                {changelog ? "Edit" : "Create"} Change Log
              </DialogTitle>
              <Button variant="ghost" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <Input
                    type="text"
                    name="version"
                    placeholder="Version"
                    id="version"
                    value={version}
                    onChange={e => setVersion(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <Input
                    type="date"
                    name="releaseDate"
                    placeholder="Release Date"
                    id="releaseDate"
                    value={releaseDate}
                    onChange={e => setReleaseDate(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                {sections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="space-y-2 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <Input
                        type="text"
                        placeholder="Section Title"
                        value={section.title}
                        onChange={e => {
                          const newSections = [...sections];
                          newSections[sectionIndex].title = e.target.value;
                          setSections(newSections);
                        }}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                      <Button
                        type="button"
                        onClick={() => removeSection(sectionIndex)}
                        className="ml-2 text-red-600 hover:text-red-700"
                        variant="ghost"
                      >
                        <X className="h-5 w-5" />
                      </Button>
                    </div>

                    <div className="flex flex-col gap-2 border border-gray-300 dark:border-gray-700 rounded-md p-2">
                      <p className="text-sm font-medium text-gray-700 dark:text-white">
                        Descriptions
                      </p>
                      {section.descriptions.map((desc, descIndex) => (
                        <div
                          key={descIndex}
                          className="flex items-center space-x-2"
                        >
                          <Input
                            type="text"
                            placeholder="Description"
                            value={desc.text}
                            onChange={e => {
                              const newSections = [...sections];
                              newSections[sectionIndex].descriptions[
                                descIndex
                              ].text = e.target.value;
                              setSections(newSections);
                            }}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                         <CustomSelect
  options={ChangeLogPointTags.map(tag => ({
    value: tag.toLowerCase(),
    label: tag,
  }))}
  value={desc.tag || ""}
  onChange={e => {
    const newSections = [...sections];
    newSections[sectionIndex].descriptions[descIndex].tag = String(e);
    setSections(newSections);
  }}
  placeholder="Tag"
/>
                          <Button
                            type="button"
                            onClick={() =>
                              removeDescription(sectionIndex, descIndex)
                            }
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash className="h-5 w-5" />
                          </Button>
                        </div>
                      ))}

                      <Button
                        type="button"
                        onClick={() => addDescription(sectionIndex)}
                        className="mt-2 w-1/3 inline-flex items-center text-sm"
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        Add Point
                      </Button>
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  onClick={addSection}
                  className="mt-4 w-1/2 inline-flex items-center text-sm"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add Section
                </Button>
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <Button
                  type="submit"
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-gray-900 dark:bg-white px-4 py-2 text-base font-medium text-white dark:text-gray-900 shadow-sm hover:bg-green-700 dark:hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                >
                  Save
                </Button>
                <Button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </div>
      </div>
    </Dialog>
  );
}
